// RELATIONSHIP FOCUS — the client is the centre. One context holds identity, pulse, brief
// (who · why · done · missing · next), and every part of the relationship. All actions open in
// place; the advisor never has to leave the person to create a follow-up, case or review.
import { useMemo, useState } from 'react';
import {
  ageOn, annualNow, searchKey, diffDays, isTerminalStage, stageIndex, caseAgeDays, missingRequirements, nextPolicyAnniversary, monthlyFromAnnual,
  archivePerson, updateNote, addHouseholdMember, CASE_MAIN_STAGES, professions, type Person, type Task,
  reviewEvidence, REVIEW_QUESTIONS, type Review, kycStatus,
} from '@vero/domain';
import { personPulse, relationshipBrief, coverageProfile, relationshipHealth, meetingPrep } from '@vero/intelligence';
import { useRuntime } from '../core/runtime.tsx';
import { Avatar, Empty, Field, Modal, useConfirm, reasonText } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon, KIND_ICON } from '../core/icons.tsx';
import { navigate, personPath, useRoute } from '../core/router.ts';
import { telLink, waLink, mailLink, mapsLink } from '../core/links.ts';
import { printMeetingPrep, printStudio, STUDIO_SECTIONS, REPORT_PRESETS } from '../core/print.ts';
import { SignatureOptIn, useSignatureChoice } from '../core/signature-choice.tsx';
import { AdvisoryTab, QuoteForm, ProposalStudio } from './advisory.tsx';
import { ConversationsTab, LogCommunication, MeetingForm } from './conversations.tsx';
import { ServiceTab, ClaimForm, ServiceForm, DocumentForm } from './service.tsx';
import { VaultTab } from './vault.tsx';
import { ProtectionTab, LifeEventForm } from './household.tsx';
import { CustomFieldsPanel } from './automations.tsx';
import { startMeetingMode } from './meetingmode.tsx';
import { GrowthTab, OpportunityForm, ReferralForm } from './growth.tsx';
import { ActionFan } from '../core/ui.tsx';
import { OutlookMailToggle } from './outlook-mail.tsx';
import { SendForSignature } from './esign.tsx';
import { WhatsAppSend } from './whatsapp.tsx';
import { VoiceSummary } from './voice-summary.tsx';
import { openMessage } from '../core/messages.tsx';
import { coverAreas, COVERAGE_AREAS } from '@vero/intelligence';
import { Dial, Band, FactorBars, Radar } from '../core/viz.tsx';
import { hueOf } from '../core/viz.tsx';
import { AttentionCard } from './attention.tsx';
import { AgendaRow } from './now.tsx';
import { openers } from './forms.tsx';
import { SinceAndSource } from './register.tsx';
import { PayslipModal } from './payslip.tsx';
import { ShareCentre } from './share.tsx';
import { PoliciesTab, PolicyCockpit } from './policies.tsx';
import { InsuredTab } from './insured.tsx';
import { KycTab } from './kyc.tsx';
import { useShown } from './setup.tsx';
import { RiskTab, RiskQuestionnaire } from './risk.tsx';
import { FamilyMapCard, FamilyLinkForm } from './family.tsx';
import { MeetingSummaryStudio, TransparencySummary } from './summaries.tsx';
import { riskMismatch, setDoNotCall, coverageForPerson, childrenOfPerson } from '@vero/domain';

const TABS = ['overview', 'policies', 'insured', 'protection', 'cases', 'advisory', 'comparisons', 'conversations', 'actions', 'notes', 'reviews', 'household', 'service', 'documents', 'kyc', 'risk', 'growth', 'records'] as const;
type Tab = typeof TABS[number];
const TAB_ICON: Record<Tab, string> = { overview: 'overview', policies: 'policy', insured: 'shield', protection: 'shield', cases: 'cases', advisory: 'radar', comparisons: 'compare', conversations: 'chat', actions: 'task', notes: 'note', reviews: 'review', household: 'household', service: 'shield', documents: 'document', kyc: 'lock', risk: 'radar', growth: 'flame', records: 'records' };

export function FocusView({ personId }: { personId: string }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const route = useRoute();
  const tab = (TABS as readonly string[]).includes(route.parts[2] ?? '') ? (route.parts[2] as Tab) : 'overview';
  const p = view.get('persons', personId);
  const confirm = useConfirm();
  const shown = useShown();
  // tabs of a licence class the advisor does not hold are not offered (the current one stays reachable by link)
  const tabs = TABS.filter((x) => x === tab || shown(`focus:${x}`));
  if (!p) return <Empty icon="people">{t('errors.person.not_found')}</Empty>;
  const pulse = personPulse(view, p.id, today);
  const brief = relationshipBrief(view, p.id, today, rt.attention);
  const o = openers(rt);
  const counts: Partial<Record<Tab, number>> = {
    policies: view.where('policies', 'personId', p.id).length, insured: view.where('insured_items', 'personId', p.id).filter((x) => x.status === 'active').length, cases: view.where('cases', 'personId', p.id).length,
    comparisons: view.where('comparisons', 'personId', p.id).length + view.where('legacy_records', 'personId', p.id).filter((r) => r.kind === 'comparison').length,
    actions: pulse.openActions, notes: view.where('notes', 'personId', p.id).length, reviews: view.where('reviews', 'personId', p.id).length,
    documents: view.where('documents', 'personId', p.id).length,
    service: view.where('service_cases', 'personId', p.id).filter((x) => x.status !== 'closed').length + view.where('claims', 'personId', p.id).filter((x) => !['paid', 'declined', 'closed'].includes(x.status)).length,
    advisory: view.where('needs_analyses', 'personId', p.id).length + view.where('quotations', 'personId', p.id).length,
    conversations: view.where('communications', 'personId', p.id).length + view.where('meetings', 'personId', p.id).length,
    growth: view.where('opportunities', 'personId', p.id).length + view.where('referrals', 'referrerId', p.id).length,
    records: view.where('legacy_records', 'personId', p.id).length,
    kyc: kycStatus(view, p.id, today).status === 'incomplete' ? 1 : 0,
    risk: riskMismatch(view, p.id, today) ? 1 : 0,
  };
  const age = p.birthDate ? ageOn(p.birthDate, today) : null;
  return (
    <div data-testid="focus">
      <button className="btn btn-ghost btn-sm" onClick={() => history.length > 1 ? history.back() : navigate('/people')} style={{ marginBottom: 10 }}><Icon name="back" />{t('common.back')}</button>
      <section className="card" style={{ ['--h' as string]: hueOf(p.displayName) }}>
        <div className="focus-head">
          <Avatar name={p.displayName} size="xl" ring={brief.why[0]?.severity} />
          <div style={{ minWidth: 0 }}>
            <div className="row wrap" style={{ gap: 8 }}>
              <span className={`chip ${p.stage === 'client' ? 'gold' : ''}`}>{i18n.enumLabel('stage', p.stage)}</span>
              {p.hot && <span className="chip high"><Icon name="flame" size={12} />{t('person.hot')}</span>}
              {p.archivedAt && <span className="chip">{t('people.archived')}</span>}
              {p.doNotCall && <span className="chip crit" data-testid="focus-dnc"><Icon name="lock" size={12} />{t('calls.dnc_on')}</span>}
              {(p.tags ?? []).slice(0, 4).map((x) => <span key={x} className="chip">{x}</span>)}
            </div>
            <h1 className="person-name" data-testid="focus-name">{p.displayName}</h1>
            <div className="focus-meta">
              {p.primaryPhone && <a href={telLink(p.primaryPhone)}><Icon name="phone" />{p.primaryPhone}</a>}
              {p.primaryEmail && <a href={mailLink(p.primaryEmail)}><Icon name="mail" />{p.primaryEmail}</a>}
              {(p.city || p.region || p.address) && <a href={mapsLink([p.address, p.city].filter(Boolean).join(', ') || p.region)} target="_blank" rel="noreferrer"><Icon name="pin" />{[p.city || p.address, p.region].filter(Boolean).join(' · ')}</a>}
              {age !== null && <span><Icon name="cake" />{t('brief.age', { age })} · {i18n.date(p.birthDate, 'dayMonth')}</span>}
              <SinceAndSource personId={p.id} />
              {p.occupation && <span>{p.occupation}{(() => { const pr = professions.findProfession(p.occupation); return pr ? <span className={`chip ${pr.risk === 'high' ? 'high' : pr.risk === 'low' ? 'ok' : ''}`} title={pr.hints.join(' · ')} style={{ marginInlineStart: 6 }}>{i18n.enumLabel('occupationRisk', pr.risk)}</span> : null; })()}</span>}
            </div>
          </div>
          <div className="quick">
            {p.primaryPhone && <a className="btn btn-sm" href={waLink(p.primaryPhone)} target="_blank" rel="noreferrer" aria-label={t('common.whatsapp')} title={t('common.whatsapp')}><Icon name="chat" /></a>}
            <button className="btn btn-sm" onClick={() => o.note(p.id)}><Icon name="note" />{t('focus.quick.note')}</button>
            <button className="btn btn-sm" onClick={() => o.followup(p.id)}><Icon name="followup" />{t('focus.quick.followup')}</button>
            <button className="btn btn-sm" onClick={() => o.appointment(p.id)}><Icon name="appointment" />{t('focus.quick.appointment')}</button>
            <button className="btn btn-sm" onClick={() => o.case(p.id)}><Icon name="cases" />{t('focus.quick.case')}</button>
            <button className="btn btn-sm" onClick={() => rt.open(<LogCommunication personId={p.id} />)} data-testid="quick-log"><Icon name="call" />{t('comms.log')}</button>
            <button className="btn btn-sm btn-primary" onClick={() => rt.open(<MeetingPrepModal personId={p.id} />)} data-testid="open-prep"><Icon name="spark" />{t('prep.open')}</button>
            {/* everything else, grouped, one click away (owner 13 Oct: fewer buttons, nothing hidden) */}
            <ActionFan groups={[
              { title: 'focus.more.contact', items: [
                ['meeting_mode.open', 'presentation', () => startMeetingMode(rt, p.id), 'open-meeting-mode'],
                ['meetings.new', 'appointment', () => rt.open(<MeetingForm personId={p.id} />)],
                ['voice.open', 'spark', () => rt.open(<VoiceSummary personId={p.id} />), 'open-voice'],
                ['voice.dictation_open', 'spark', () => rt.open(<VoiceSummary personId={p.id} mode="dictation" />), 'open-dictation'],
                ['summary.meeting_open', 'document', () => rt.open(<MeetingSummaryStudio personId={p.id} />), 'open-meeting-summary'],
                [p.doNotCall ? 'calls.dnc_clear' : 'calls.dnc_mark', 'lock', () => void rt.run((v, c) => setDoNotCall(v, { personId: p.id }, !p.doNotCall, c), p.doNotCall ? 'calls.dnc_off_done' : 'calls.dnc_on_done'), 'open-dnc'],
                ['share.open', 'mail', () => rt.open(<ShareCentre personId={p.id} />), 'open-share'],
                ['templates.compose_template', 'chat', () => openMessage(rt, p.id, 'campaign'), 'open-compose'],
                ['whatsapp.send', 'chat', () => rt.open(<WhatsAppSend personId={p.id} />), 'open-whatsapp'],
              ] },
              { title: 'focus.more.advice', items: [
                ['focus.quick.review', 'review', () => o.review(p.id), 'open-review'],
                ['needs.new', 'radar', () => navigate(`/needs/new?person=${p.id}`)],
                ['risk.questionnaire', 'radar', () => rt.open(<RiskQuestionnaire personId={p.id} />), 'open-risk'],
                ['quotes.new', 'document', () => rt.open(<QuoteForm personId={p.id} />)],
                ['proposal.new', 'spark', () => rt.open(<ProposalStudio personId={p.id} />)],
                ['comparator.new', 'compare', () => navigate(`/comparator/new?person=${p.id}`)],
                ['pipeline.new', 'flame', () => rt.open(<OpportunityForm personId={p.id} />)],
                ['referrals.new', 'people', () => rt.open(<ReferralForm referrerId={p.id} />)],
              ] },
              { title: 'focus.more.service', items: [
                ['service.new', 'shield', () => rt.open(<ServiceForm personId={p.id} />)],
                ['claims.new', 'policy', () => rt.open(<ClaimForm personId={p.id} />)],
                ['lifeEvent.new', 'heart', () => rt.open(<LifeEventForm personId={p.id} />)],
                ['family.add', 'household', () => rt.open(<FamilyLinkForm personId={p.id} />), 'open-family'],
                ['summary.transparency_open', 'document', () => rt.open(<TransparencySummary personId={p.id} />), 'open-transparency'],
              ] },
              { title: 'focus.more.docs', items: [
                ['documents.new', 'document', () => rt.open(<DocumentForm personId={p.id} />)],
                ['vault.upload', 'upload', () => navigate(`/vault?upload=1&person=${p.id}`)],
                ['esign.send', 'document', () => rt.open(<SendForSignature personId={p.id} />), 'open-esign'],
                ['payslip.open', 'coins', () => rt.open(<PayslipModal personId={p.id} />), 'open-payslip'],
                ['taxc.title', 'coins', () => navigate(`/tax?person=${p.id}`)],
              ] },
            ]} />
            <button className="btn btn-sm btn-ghost" onClick={() => o.person(p)} aria-label={t('common.edit')} title={t('common.edit')}><Icon name="settings" /></button>
            <button className="btn btn-sm btn-ghost" onClick={() => rt.open(<PrintStudioModal personId={p.id} />)} aria-label={t('print.studio')} title={t('print.studio')} data-testid="print-studio"><Icon name="print" /></button>
          </div>
        </div>
        <div className="pulse" data-testid="pulse">
          <div><span>{t('pulse.policies')}</span><b>{pulse.activePolicies}</b></div>
          <div><span>{t('pulse.portfolio')}</span><b>{i18n.money(pulse.annualPortfolio)}</b></div>
          <div><span>{t('pulse.open_actions')}</span><b>{pulse.openActions}</b></div>
          <div><span>{t('pulse.review')}</span><b style={{ color: pulse.review.state === 'overdue' || pulse.review.state === 'never' ? 'var(--high)' : undefined }}>{i18n.enumLabel('reviewState', pulse.review.state)}</b></div>
          <div><span>{t('pulse.cases')}</span><b>{pulse.activeCases}</b></div>
          <div><span>{t('pulse.next_move')}</span><b title={pulse.nextMove?.title}>{pulse.nextMove ? `${pulse.nextMove.dueDate ? i18n.date(pulse.nextMove.dueDate, 'dayMonth') + ' · ' : ''}${i18n.maybe(pulse.nextMove.title)}` : t('pulse.none')}</b></div>
        </div>
      </section>

      <nav className="ribbon" role="tablist" aria-label={t('brief.title')}>
        {tabs.map((x) => (
          <button key={x} role="tab" aria-selected={tab === x} onClick={() => navigate(personPath(p.id, x === 'overview' ? undefined : x), { replace: true })}>
            <Icon name={TAB_ICON[x]} size={16} />{t(`focus.tabs.${x}`)}{counts[x] ? <span className="count">{counts[x]}</span> : null}
          </button>
        ))}
      </nav>

      {tab === 'overview' && <Overview person={p} brief={brief} />}
      {tab === 'policies' && <PoliciesTab person={p} />}
      {tab === 'insured' && <InsuredTab person={p} />}
      {tab === 'protection' && <ProtectionTab person={p} />}
      {tab === 'cases' && <CasesTab person={p} />}
      {tab === 'comparisons' && <ComparisonsTab person={p} />}
      {tab === 'actions' && <ActionsTab person={p} />}
      {tab === 'notes' && <NotesTab person={p} />}
      {tab === 'reviews' && <ReviewsTab person={p} />}
      {tab === 'household' && <div className="stack" style={{ gap: 18 }}><FamilyMapCard person={p} /><HouseholdTab person={p} /></div>}
      {tab === 'advisory' && <AdvisoryTab personId={p.id} />}
      {tab === 'conversations' && <ConversationsTab personId={p.id} />}
      {tab === 'service' && <ServiceTab personId={p.id} />}
      {tab === 'documents' && <VaultTab personId={p.id} />}
      {tab === 'kyc' && <KycTab person={p} />}
      {tab === 'risk' && <RiskTab person={p} />}
      {tab === 'growth' && <GrowthTab personId={p.id} />}
      {tab === 'records' && <><CustomFieldsPanel entity="persons" id={p.id} /><RecordsTab person={p} /></>}

      {!p.archivedAt && tab === 'overview' && (
        <div style={{ marginTop: 18, textAlign: 'end' }}>
          <button className="btn btn-ghost btn-sm btn-danger" onClick={() => confirm(t('person.archive_confirm', { name: p.displayName }), () => rt.run((v, c) => archivePerson(v, p.id, c), 'common.saved'), true)}>{t('person.archive')}</button>
        </div>
      )}
    </div>
  );
}

function Overview({ person, brief }: { person: Person; brief: ReturnType<typeof relationshipBrief> }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const [tf, setTf] = useState<string>('all');
  const [tq, setTq] = useState('');
  const allEvents = useMemo(() => [...view.where('timeline', 'personId', person.id)].sort((a, b) => b.at.localeCompare(a.at)), [view, person.id]);
  const types = [...new Set(allEvents.map((e) => e.type))];
  const timeline = allEvents.filter((e) => (tf === 'all' || e.type === tf) && (!tq || searchKey(`${i18n.maybe(e.title)} ${e.body ?? ''}`).includes(searchKey(tq)))).slice(0, 80);
  const lc = brief.done.lastContact;
  return (
    <div className="grid grid-now">
      <div className="stack" style={{ alignContent: 'start' }}>
        {person.stage !== 'dependent' && <Vitals person={person} />}
        <section className="card" data-testid="brief">
          <div className="card-head"><h2>{t('brief.title')}</h2></div>
          <div className="brief">
            <div><h4>{t('brief.who')}</h4>
              <div>{[i18n.enumLabel('stage', person.stage), person.occupation, person.maritalStatus].filter(Boolean).join(' · ')}</div>
              <div className="small muted">{brief.done.since ? t('brief.since', { date: i18n.date(brief.done.since) }) : ''}</div>
            </div>
            <div><h4>{t('brief.why')}</h4>
              {brief.why.length ? <div className="small">{t('brief.open_items', { count: brief.why.length })}</div> : <div className="small muted">{t('brief.no_reason')}</div>}
            </div>
            <div><h4>{t('brief.done')}</h4>
              <div className="small">{lc ? t('brief.last_contact', { date: i18n.date(lc.at), what: `${i18n.enumLabel('timeline', lc.type)} — ${i18n.maybe(lc.title)}` }) : t('brief.never_contacted')}</div>
            </div>
            <div><h4>{t('brief.missing')}</h4>
              {brief.missing.length ? <div className="row wrap" style={{ gap: 6 }}>{brief.missing.map((m, i) => <span key={i} className="chip">{i18n.maybe(m.key)}</span>)}</div> : <div className="small muted">{t('brief.nothing_missing')}</div>}
            </div>
            <div className="next"><h4>{t('brief.next')}</h4>
              {brief.next ? <AttentionCard items={[brief.next]} compact /> : <div className="small muted">{t('brief.no_reason')}</div>}
            </div>
          </div>
        </section>
        {brief.why.length > 1 && (
          <section className="card"><div className="card-head"><h3>{t('focus.attention')}</h3></div>
            <div className="attention-list">{brief.why.slice(1).map((it) => <AttentionCard key={it.id} items={[it]} compact />)}</div>
          </section>
        )}
        {person.notes && <section className="card card-pad"><div className="upper">{t('person.notes')}</div><p style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{person.notes}</p></section>}
      </div>
      <section className="card" style={{ alignSelf: 'start' }}>
        <div className="card-head"><h3>{t('focus.timeline')}</h3><span className="tiny muted">{allEvents.length}</span></div>
        <OutlookMailToggle personId={person.id} />
        <div className="timeline-filter">
          <input className="input" type="search" placeholder={t('timeline.search')} value={tq} onChange={(e: { target: HTMLInputElement }) => setTq(e.target.value)} aria-label={t('timeline.search')} data-testid="timeline-search" />
          <div className="row wrap" style={{ gap: 4 }}><button className="chip" aria-pressed={tf === 'all'} onClick={() => setTf('all')}>{t('common.all')}</button>{types.map((x) => <button key={x} className="chip" aria-pressed={tf === x} onClick={() => setTf(x)}>{i18n.enumLabel('timeline', x)}</button>)}</div>
        </div>
        {timeline.length ? (
          <ol className="timeline">{timeline.map((e) => (
            <li key={e.id}>
              <time>{i18n.dateTime(e.at)} · {i18n.enumLabel('timeline', e.type)}</time>
              <div style={{ fontWeight: 600 }}>{i18n.maybe(e.title, { version: e.body ?? '' })}</div>
              {e.body && !e.body.startsWith('stage:') && !e.body.startsWith('household:') && e.type !== 'import' && <div className="small muted" style={{ whiteSpace: 'pre-wrap' }}>{e.body}</div>}
              {e.body?.startsWith('stage:') && <span className="chip teal">{i18n.enumLabel('caseStage', e.body.slice(6))}</span>}
              {(e.data as { source?: string } | null)?.source === 'outlook' && (() => { const d = e.data as { direction?: string; link?: string | null }; return <div className="small muted row" style={{ gap: 8 }} data-testid="timeline-outlook"><span className="chip">{t(d.direction === 'out' ? 'outlook.mail.sent' : 'outlook.mail.received')}</span>{d.link && <a href={d.link} target="_blank" rel="noopener noreferrer">{t('outlook.mail.open')}</a>}</div>; })()}
              {e.refTable === 'tasks' && (() => { const tk = view.get('tasks', e.refId); return tk?.dueDate && e.type !== 'call' && e.type !== 'meeting' ? <div className="small muted">{i18n.date(tk.dueDate, 'weekday')}{tk.dueTime ? ` · ${i18n.time(tk.dueTime)}` : ''}</div> : null; })()}
            </li>
          ))}</ol>
        ) : <Empty icon="overview">{t('focus.timeline_empty')}</Empty>}
      </section>
    </div>
  );
}

function CasesTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const cases = [...view.where('cases', 'personId', person.id)].sort((a, b) => Number(isTerminalStage(a.stage)) - Number(isTerminalStage(b.stage)) || b.updatedAt.localeCompare(a.updatedAt));
  return (
    <div className="stack">
      <div className="row spread"><span /><button className="btn btn-primary" onClick={() => openers(rt).case(person.id)}><Icon name="plus" />{t('case.new')}</button></div>
      {cases.length ? cases.map((c) => {
        const missing = missingRequirements(view.where('case_requirements', 'caseId', c.id)).length;
        const idx = stageIndex(c.stage);
        return (
          <article key={c.id} className="card card-pad" onClick={() => navigate(`/cases/${c.id}`)} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
            <div className="row spread wrap"><div><b>{c.product}</b> <span className="muted small">{[c.insurer, c.applicationNumber].filter(Boolean).join(' · ')}</span></div><span className="chip teal">{i18n.enumLabel('caseStage', c.stage)}</span></div>
            <div className="journey" style={{ margin: '12px 0' }}>{CASE_MAIN_STAGES.map((s, i) => <i key={s} className={i < idx ? 'on' : i === idx ? 'now' : ''} />)}</div>
            <div className="row wrap small muted"><span>{t('case.missing', { count: missing })}</span>·<span>{c.nextAction ? `${c.nextAction}${c.nextActionDate ? ' · ' + i18n.date(c.nextActionDate, 'dayMonth') : ''}` : '—'}</span>·<span>{t('case.age', { days: caseAgeDays(c, today) })}</span></div>
          </article>
        );
      }) : <div className="card"><Empty icon="cases">{t('case.empty')}</Empty></div>}
    </div>
  );
}

function ActionsTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const tasks = view.where('tasks', 'personId', person.id);
  const sortKey = (x: Task) => `${x.dueDate ?? '9999'}${x.dueTime ?? ''}`;
  const open = tasks.filter((x) => x.status === 'open').sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  const done = tasks.filter((x) => x.status !== 'open').sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')).slice(0, 30);
  return (
    <div className="grid grid-2">
      <section className="card">
        <div className="card-head"><h3>{t('enum.taskStatus.open')} · {open.length}</h3><div className="row"><button className="btn btn-sm" onClick={() => openers(rt).followup(person.id)}>{t('focus.quick.followup')}</button><button className="btn btn-sm" onClick={() => openers(rt).appointment(person.id)}>{t('focus.quick.appointment')}</button></div></div>
        {open.length ? <div className="agenda-list">{open.map((x) => <TaskLine key={x.id} task={x} />)}</div> : <Empty icon="task">{t('common.empty')}</Empty>}
      </section>
      <section className="card">
        <div className="card-head"><h3>{t('enum.taskStatus.done')}</h3></div>
        {done.length ? <div className="agenda-list">{done.map((x) => <AgendaRow key={x.id} task={x} />)}</div> : <Empty icon="check">{t('common.empty')}</Empty>}
      </section>
    </div>
  );
}

function TaskLine({ task }: { task: Task }) {
  const rt = useRuntime();
  const overdue = task.dueDate && task.dueDate < rt.today;
  return (
    <div className="agenda-item" onClick={() => openers(rt).complete(task)} role="button" tabIndex={0}>
      <time style={{ color: overdue ? 'var(--crit)' : undefined }}>{task.dueDate ? rt.i18n.date(task.dueDate, 'dayMonth') : '—'}</time>
      <div style={{ minWidth: 0 }}><div className="t person-name one-line">{rt.i18n.maybe(task.title)}</div><div className="s">{[rt.i18n.enumLabel('taskKind', task.kind), task.dueTime ? rt.i18n.time(task.dueTime) : '', rt.i18n.enumLabel('priority', task.priority)].filter(Boolean).join(' · ')}</div></div>
      <span className="kind-icon"><Icon name={KIND_ICON[task.kind] ?? 'task'} /></span>
    </div>
  );
}

function NotesTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const notes = [...view.where('notes', 'personId', person.id)].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || b.createdAt.localeCompare(a.createdAt));
  return (
    <div className="stack">
      <div className="row spread"><span /><button className="btn btn-primary" onClick={() => openers(rt).note(person.id)}><Icon name="plus" />{t('note.new')}</button></div>
      {notes.length ? notes.map((n) => (
        <article key={n.id} className="card card-pad" data-testid="note">
          <div className="row spread"><b>{n.title || i18n.date(n.createdAt)}</b><div className="row"><span className="small muted">{i18n.dateTime(n.createdAt)}</span><button className="btn btn-sm btn-ghost" aria-pressed={!!n.pinned} onClick={() => rt.run((v, c) => updateNote(v, n.id, { pinned: !n.pinned }, c))}><Icon name="pin" size={14} />{n.pinned ? t('note.pinned') : ''}</button></div></div>
          <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0' }}>{n.body}</p>
        </article>
      )) : <div className="card"><Empty icon="note">{t('note.empty')}</Empty></div>}
    </div>
  );
}

function ReviewsTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const pulse = personPulse(view, person.id, today);
  const reviews = [...view.where('reviews', 'personId', person.id)].sort((a, b) => b.date.localeCompare(a.date));
  return (
    <div className="stack">
      <section className="card card-pad row spread wrap">
        <div><div className="upper">{t('pulse.review')}</div><b style={{ fontSize: 18 }}>{i18n.enumLabel('reviewState', pulse.review.state)}</b>
          <div className="small muted">{[pulse.review.lastReviewDate ? t('review.last', { date: i18n.date(pulse.review.lastReviewDate) }) : '', pulse.review.dueDate ? t('review.due', { date: i18n.date(pulse.review.dueDate) }) : ''].filter(Boolean).join(' · ')}</div></div>
        <button className="btn btn-primary" onClick={() => openers(rt).review(person.id)}><Icon name="review" />{t('review.complete')}</button>
      </section>
      <section className="card"><div className="card-head"><h3>{t('review.history')}</h3></div>
        {reviews.length ? <ol className="timeline">{reviews.map((r) => <li key={r.id}><time>{i18n.date(r.date)} · {i18n.enumLabel('reviewOutcome', r.outcome)}</time>{r.notes && <div style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</div>}<ReviewEvidenceLine r={r} />{r.nextReviewDate && <div className="small muted">{t('review.next_date')}: {i18n.date(r.nextReviewDate)}</div>}</li>)}</ol> : <Empty icon="review">{t('review.empty')}</Empty>}
      </section>
    </div>
  );
}

function HouseholdTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const members = person.householdId ? view.where('household_members', 'householdId', person.householdId).filter((m) => m.personId !== person.id) : [];
  const add = () => rt.open(<AddChild personId={person.id} />);
  return (
    <section className="card">
      <div className="card-head"><h3>{t('household.children')}</h3><button className="btn btn-sm btn-primary" onClick={add}><Icon name="plus" />{t('household.add_child')}</button></div>
      {members.length ? <div className="agenda-list">{members.map((m) => {
        const x = view.get('persons', m.personId);
        if (!x) return null;
        // policies in force today that insure this member (any insured party), shared rule: coverageForPerson
        const insured = coverageForPerson(view, x.id, today).map((c) => c.policy);
        return (
          <div key={m.id} className="agenda-item" onClick={() => x.stage !== 'dependent' && navigate(personPath(x.id))}>
            <Avatar name={x.displayName} size="sm" />
            <div style={{ minWidth: 0 }}><div className="t person-name">{x.displayName}</div><div className="s">{[i18n.enumLabel('householdRole', m.role), x.birthDate ? t('household.age', { age: ageOn(x.birthDate, today) }) : '', insured.map((i) => i.productName).join(', ')].filter(Boolean).join(' · ')}</div></div>
            {insured.length ? <span className="chip ok"><Icon name="shield" size={12} />{insured.length}</span> : <span className="chip">—</span>}
          </div>
        );
      })}</div> : <Empty icon="household">{t('household.empty')}</Empty>}
      <FamilyCoverage person={person} />
    </section>
  );
}

/** Household 360 (spec §5): who in the family is covered for what — one matrix, gaps visible. */
function FamilyCoverage({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, view, today } = rt;
  // household members plus children recorded in the Family tab, once each
  const ids = new Set<string>([person.id]);
  const members = [person];
  const push = (id: string | null | undefined) => { const x = id ? view.get('persons', id) : undefined; if (x && !x.archivedAt && !ids.has(x.id)) { ids.add(x.id); members.push(x); } };
  if (person.householdId) for (const m of view.where('household_members', 'householdId', person.householdId)) push(m.personId);
  for (const k of childrenOfPerson(view, person.id, today)) push(k.personId);
  if (members.length < 2) return null;
  // each member's own cover today: policies on which they are an insured person (coverageForPerson), with their own benefits
  const covered = (m: Person) => new Set(coverageForPerson(view, m.id, today).flatMap((c) => coverAreas(c)));
  return (
    <div style={{ padding: '4px 20px 18px', overflowX: 'auto' }} data-testid="family-coverage">
      <div className="upper" style={{ margin: '6px 0 8px' }}>{t('household.coverage')}</div>
      <table className="table family-matrix">
        <thead><tr><th />{COVERAGE_AREAS.map((a) => <th key={a} style={{ textAlign: 'center' }}>{t(`coverage.area.${a}`)}</th>)}</tr></thead>
        <tbody>{members.map((m) => { const c = covered(m); return <tr key={m.id}><td className="person-name one-line" style={{ maxWidth: 180 }}>{m.displayName}</td>{COVERAGE_AREAS.map((a) => <td key={a} style={{ textAlign: 'center' }}>{c.has(a) ? <span className="cov yes" title={t('coverage.held.1')}>●</span> : <span className="cov no" title={t('coverage.held.0')}>○</span>}</td>)}</tr>; })}</tbody>
      </table>
    </div>
  );
}

/** Policy Cockpit (spec §6.2): one policy, everything about it, every action in place. */

function PrintStudioModal({ personId }: { personId: string }) {
  const rt = useRuntime();
  const { t } = rt;
  const [sel, setSel] = useState<string[]>(['identity', 'policies', 'coverage', 'next_actions']);
  const [signed, setSigned] = useSignatureChoice('report');
  return (
    <Modal title={t('print.studio')} footer={<><SignatureOptIn doc="report" value={signed} onChange={setSigned} /><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={() => { const preset = Object.keys(REPORT_PRESETS).find((k) => JSON.stringify(REPORT_PRESETS[k]) === JSON.stringify(sel)); printStudio(rt, personId, sel, preset ? t(`print.report.${preset}`) : undefined, { signature: signed }); rt.close(); }} data-testid="studio-print"><Icon name="print" />{t('common.print')}</button></>}>
      <div className="stack" data-testid="print-studio-modal">
        <p className="small muted" style={{ margin: 0 }}>{t('print.studio_hint')}</p>
        <div className="row wrap" style={{ gap: 6 }} data-testid="report-presets">{Object.keys(REPORT_PRESETS).map((k) => <button key={k} className={`chip toggle ${JSON.stringify(REPORT_PRESETS[k]) === JSON.stringify(sel) ? 'on' : ''}`} onClick={() => setSel(REPORT_PRESETS[k]!)} data-testid={`preset-${k}`}>{t(`print.report.${k}`)}</button>)}</div>
        <div className="row wrap">{STUDIO_SECTIONS.map((s) => <label key={s} className="check chip" style={{ height: 32 }}><input type="checkbox" checked={sel.includes(s)} onChange={() => setSel(sel.includes(s) ? sel.filter((x) => x !== s) : [...sel, s])} />{t(`print.section.${s}`)}</label>)}</div>
      </div>
    </Modal>
  );
}

function AddChild({ personId }: { personId: string }) {
  const rt = useRuntime();
  const [name, setName] = useState('');
  const [birthDate, setBirth] = useState('');
  const save = async () => { if (await rt.run((v, c) => addHouseholdMember(v, personId, { name, birthDate: birthDate || null, role: 'child' }, c), 'common.saved')) rt.close(); };
  return (
    <Modal title={rt.t('household.add_child')} footer={<><button className="btn" onClick={rt.close}>{rt.t('common.cancel')}</button><button className="btn btn-primary" onClick={save}>{rt.t('common.save')}</button></>}>
      <div className="form-grid">
        <Field label={rt.t('household.child_name')}><input className="input" value={name} onChange={(e: { target: HTMLInputElement }) => setName(e.target.value)} autoFocus /></Field>
        <Field label={rt.t('person.birth_date')} group><DateField kind="birth" value={birthDate} onChange={(d) => setBirth(d)} /></Field>
      </div>
    </Modal>
  );
}

function RecordsTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const recs = view.where('legacy_records', 'personId', person.id);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="card">
      <div className="card-head"><div><h3>{t('records.title')}</h3><div className="small muted">{t('records.subtitle')}</div></div></div>
      {recs.length ? <div className="agenda-list">{recs.map((r) => (
        <div key={r.id}>
          <div className="agenda-item" onClick={() => setOpen(open === r.id ? null : r.id)} role="button" tabIndex={0}>
            <span className="kind-icon"><Icon name="records" /></span>
            <div style={{ minWidth: 0 }}><div className="t">{t(`records.kind.${r.kind}`)}{r.title ? ` · ${r.title}` : ''}</div><div className="s">{[r.date ? i18n.date(r.date) : '', r.sourceVersion].filter(Boolean).join(' · ')}</div></div>
            <Icon name="arrow" size={16} />
          </div>
          {open === r.id && <pre className="code" style={{ margin: '0 10px 10px' }}>{JSON.stringify(r.payload, null, 2)}</pre>}
        </div>
      ))}</div> : <Empty icon="records">{t('records.empty')}</Empty>}
    </section>
  );
}

/** Relationship Health (explained) + Coverage Radar (needs vs. held, with the reason for every need). */
function Vitals({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, view, today } = rt;
  const health = useMemo(() => (person.stage === 'client' ? relationshipHealth(view, person.id, today) : null), [view, person.id, person.stage, today]);
  const cov = useMemo(() => coverageProfile(view, person.id, today), [view, person.id, today]);
  const [active, setActive] = useState<string | null>(null);
  const heldKey = (h: number) => (h >= 1 ? 'coverage.held.1' : h > 0 ? 'coverage.held.0.5' : 'coverage.held.0');
  return (
    <section className="card" data-testid="vitals">
      {health && (
        <div className="vitals" data-testid="health">
          <Dial score={health.score} band={health.band} size={96} stroke={9} label={t(`health.band.${health.band}`)} testId="health-dial" />
          <div className="stack" style={{ gap: 8 }}>
            <div className="row spread"><b>{t('health.title')}</b><Band band={health.band} label={t(`health.band.${health.band}`)} /></div>
            <FactorBars rows={health.factors.map((f) => ({ key: f.key, label: t(`health.factor.${f.key}`), value: f.points, max: f.max, hint: rt.i18n.t(f.detailKey, Object.fromEntries(Object.entries(f.params ?? {}).map(([k, x]) => [k, typeof x === 'string' ? rt.i18n.maybe(x) : x]))) }))} />
            <div className="tiny muted">{health.factors.map((f) => rt.i18n.t(f.detailKey, f.params ?? {})).join(' · ')}</div>
          </div>
        </div>
      )}
      <div className="card-head" style={{ paddingTop: health ? 4 : 16 }}><div><h3>{t('coverage.title')}</h3><div className="small muted">{t('coverage.subtitle')}</div></div><span className={`chip ${cov.gaps.length ? 'crit' : 'ok'}`} data-testid="coverage-gaps">{t('coverage.gaps', { count: cov.gaps.length })}</span></div>
      <div className="radar-wrap" data-testid="radar">
        <Radar areas={cov.areas.map((a) => ({ key: a.area, label: t(`coverage.area.${a.area}`), need: a.need, held: a.held, gap: a.gap }))} active={active} onActive={setActive} needLabel={t('coverage.need_series')} heldLabel={t('coverage.held_series')} />
        <div className="radar-detail">
          {cov.areas.map((a) => (
            <div key={a.area} className="radar-area" data-active={active === a.area} onMouseEnter={() => setActive(a.area)} onMouseLeave={() => setActive(null)}>
              <i style={{ background: a.gap ? 'var(--st-crit)' : a.held >= 1 ? 'var(--s3)' : a.held > 0 ? 'var(--st-warn)' : 'var(--grid)' }} />
              <div style={{ minWidth: 0 }}><b>{t(`coverage.area.${a.area}`)}</b><small>{t(`coverage.need.${a.need}`)} · {t(a.needReason)}</small></div>
              <span className={`chip ${a.gap ? 'crit' : a.held >= 1 ? 'ok' : a.held > 0 ? 'gold' : ''}`}>{t(heldKey(a.held))}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonsTab({ person }: { person: Person }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const list = [...view.where('comparisons', 'personId', person.id)].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const legacy = view.where('legacy_records', 'personId', person.id).filter((r) => r.kind === 'comparison');
  return (
    <div className="stack">
      <div className="row spread"><span className="muted small">{t('comparator.subtitle')}</span><button className="btn btn-primary" onClick={() => navigate(`/comparator/new?person=${person.id}`)} data-testid="new-comparison"><Icon name="plus" />{t('comparator.new')}</button></div>
      {list.length ? list.map((c) => (
        <article key={c.id} className="card card-pad row spread wrap" style={{ cursor: 'pointer' }} onClick={() => navigate(`/comparator/${c.id}`)} role="button" tabIndex={0}>
          <div style={{ minWidth: 0 }}><b>{c.title}</b><div className="small muted">{i18n.dateTime(c.updatedAt)}</div></div>
          {c.status === 'decided' ? <span className="chip ok"><Icon name="lock" size={12} />{t(`comparator.decisions.${c.decision ?? 'none'}`)}</span> : <span className="chip gold">{t('comparator.draft')}</span>}
        </article>
      )) : !legacy.length && <div className="card"><Empty icon="compare">{t('comparator.empty')}</Empty></div>}
      {legacy.length > 0 && <section className="card"><div className="card-head"><h3>{t('comparator.legacy')}</h3></div>
        <div className="agenda-list">{legacy.map((r) => <div key={r.id} className="agenda-item" onClick={() => navigate(personPath(person.id, 'records'))} role="button" tabIndex={0}><span className="kind-icon"><Icon name="records" /></span><div style={{ minWidth: 0 }}><div className="t">{r.title || t('records.kind.comparison')}</div><div className="s">{r.date ? i18n.date(r.date) : ''}</div></div><Icon name="arrow" size={16} /></div>)}</div>
      </section>}
    </div>
  );
}

export function MeetingPrepModal({ personId }: { personId: string }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const prep = useMemo(() => meetingPrep(view, personId, today, rt.attention), [view, personId, today, rt.attention]);
  const p = prep.person;
  const pointText = (pt: typeof prep.points[number]) => pt.item ? t('prep.point_moment', { what: reasonText(rt.i18n, pt.item) })
    : t(pt.key, Object.fromEntries(Object.entries(pt.params).map(([k, x]) => [k, k === 'date' && typeof x === 'string' ? i18n.date(x) : typeof x === 'string' ? i18n.maybe(x) : x])));
  const since = p.createdAt ? i18n.date(p.createdAt) : '';
  return (
    <Modal wide title={`${t('prep.title')} · ${p.displayName}`} footer={<><button className="btn" onClick={rt.close}>{t('common.close')}</button><button className="btn btn-primary" onClick={() => printMeetingPrep(rt, prep, prep.points.map(pointText))}><Icon name="print" />{t('common.print')}</button></>}>
      <div className="prep" data-testid="prep">
        <div className="row" style={{ gap: 16 }}>
          <Avatar name={p.displayName} size="lg" />
          <div className="grow"><b style={{ fontSize: 18 }}>{p.displayName}</b><div className="small muted">{[i18n.enumLabel('stage', p.stage), p.occupation, p.birthDate ? t('brief.age', { age: ageOn(p.birthDate, today) }) : '', since ? t('prep.since', { date: since }) : ''].filter(Boolean).join(' · ')}</div></div>
          {prep.health && <Dial score={prep.health.score} band={prep.health.band} size={72} stroke={7} />}
        </div>
        <h3>{t('prep.talking')}</h3>
        {prep.points.length ? <ul data-testid="prep-points">{prep.points.map((pt, i) => <li key={i}>{pointText(pt)}</li>)}</ul> : <p className="muted">{t('prep.none')}</p>}
        <div className="grid grid-2">
          <div><h3>{t('prep.holdings')} · {i18n.money(prep.totalAnnual)}</h3>
            {prep.holdings.length ? <table className="table"><tbody>{prep.holdings.map((h) => <tr key={h.policy.id}><td>{h.policy.productName}<div className="tiny muted">{h.policy.carrier}</div></td><td className="num">{i18n.money(h.annual)}</td></tr>)}</tbody></table> : <p className="muted small">{t('policy.empty')}</p>}
          </div>
          <div><h3>{t('prep.family')}</h3>
            {prep.family.length ? <ul>{prep.family.map((f) => <li key={f.person.id}>{f.person.displayName} · {i18n.enumLabel('householdRole', f.role)}{f.person.birthDate ? ` · ${t('household.age', { age: ageOn(f.person.birthDate, today) })}` : ''}</li>)}</ul> : <p className="muted small">{t('household.empty')}</p>}
            <h3>{t('prep.open_items')}</h3>
            {prep.openTasks.length ? <ul>{prep.openTasks.slice(0, 8).map((x) => <li key={x.id}>{x.dueDate ? i18n.date(x.dueDate, 'dayMonth') + ' · ' : ''}{i18n.maybe(x.title)}</li>)}</ul> : <p className="muted small">{t('common.empty')}</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** v5 review evidence: what was asked and answered, the portfolio at the time, and what needed action. */
function ReviewEvidenceLine({ r }: { r: Review }) {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const ev = reviewEvidence(r);
  if (!ev.answered && r.portfolioAnnual == null) return null;
  const answers = (r.answers ?? {}) as Record<string, { a: string; note?: string | null }>;
  return (
    <details className="small review-evidence" data-testid="review-evidence">
      <summary>{t('review.evidence', { answered: ev.answered, total: REVIEW_QUESTIONS.length })}{r.portfolioAnnual != null ? ` · ${t('review.portfolio_then', { amount: i18n.money(r.portfolioAnnual) })}` : ''}{ev.actions.length ? ` · ${t('review.actions_count', { count: ev.actions.length })}` : ''}</summary>
      <ul>{REVIEW_QUESTIONS.filter((q) => answers[q]).map((q) => <li key={q} className={ev.actions.includes(q) ? 'act' : ''}>{t(`review.q.${q}`)} — <b>{t(`review.a.${answers[q]!.a}`)}</b>{answers[q]!.note ? ` · ${answers[q]!.note}` : ''}</li>)}</ul>
    </details>
  );
}
